import React, { useState } from 'react';
import { Bars3Icon, XMarkIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { trackEvent } from '../lib/metaPixel';
import { getCataloguePath } from '../features/training/trainingRoutes';
import { getMentoringContent } from '../features/mentoring/mentoringContent';
import { getMentoringPath } from '../features/mentoring/mentoringRoutes';
import { BUILDER_URL } from '../lib/externalLinks';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsMenuOpen(false);
  };

  const locale = i18n.language.startsWith('en') ? 'en' : 'fr';
  const trainingCataloguePath = getCataloguePath(locale);
  const mentoringPath = getMentoringPath(locale, 'index');

  const navItems = [
    { name: t('header.nav.home'), href: './' },
    { name: t('header.nav.services'), href: './#services' },
    { name: t('header.nav.industries'), href: './#industries' },
    { name: t('header.nav.values'), href: './#values' },
    { name: t('training.navLabel'), href: trainingCataloguePath, isRoute: true },
    { name: getMentoringContent(locale)?.nav.navLabel, href: mentoringPath, isRoute: true },
    { name: t('proEmail.navLabel'), href: '/pro-email', isRoute: true },
    { name: t('talents.navLabel'), href: '/talents', isRoute: true },
    { name: t('contact.navLabel'), href: '/contact', isRoute: true },
  ].filter((item) => Boolean(item.name && item.href));

  return (
    <header className="fixed top-0 w-full bg-white/95 backdrop-blur-md border-b border-gray-100 z-50 animate-slide-down">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center space-x-3 hover:scale-105 transition-transform duration-300">
            <img
              src="/favicon.svg"
              alt="CSL Brands Logo"
              className="w-16 h-10 object-contain"
            />
          </div>

          {/* Desktop Navigation. The mentoring item makes nine, and French needs
              ~790px of nav, so the burger takes over below 1280px rather than
              letting the items clip against the logo and the CTA. */}
          <nav className="hidden xl:flex min-w-0 items-center gap-3 whitespace-nowrap">
            {navItems.map((item, index) =>
              item.isRoute ? (
                <Link
                  key={item.name}
                  to={item.href}
                  className="shrink-0 whitespace-nowrap text-gray-700 hover:text-csl-green transition-colors duration-300 font-medium hover:scale-105 animate-fade-in [animation-delay:calc(100ms*var(--index))]"
                  style={{ ['--index']: index }}
                >
                  {item.name}
                </Link>
              ) : (
                <a
                  key={item.name}
                  href={item.href}
                  className="shrink-0 whitespace-nowrap text-gray-700 hover:text-csl-green transition-colors duration-300 font-medium hover:scale-105 animate-fade-in [animation-delay:calc(100ms*var(--index))]"
                  style={{ ['--index']: index }}
                >
                  {item.name}
                </a>
              )
            )}
          </nav>

          <div className="hidden xl:flex shrink-0 items-center space-x-4">
            {/* Language Switcher */}
            <div className="flex items-center space-x-2 text-sm font-medium text-gray-600">
              <GlobeAltIcon className="h-5 w-5" />
              <button
                onClick={() => changeLanguage('fr')}
                className={`hover:text-csl-green transition-colors ${i18n.language.startsWith('fr') ? 'text-csl-green font-bold' : ''}`}
              >
                FR
              </button>
              <span>|</span>
              <button
                onClick={() => changeLanguage('en')}
                className={`hover:text-csl-green transition-colors ${i18n.language.startsWith('en') ? 'text-csl-green font-bold' : ''}`}
              >
                EN
              </button>
            </div>

            {/* CTA Button */}
            <a
              href={BUILDER_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('Lead', { customData: { content_name: 'builder-header-cta' } })}
              className="hidden 2xl:inline-flex items-center justify-center whitespace-nowrap px-4 py-2 bg-csl-green text-white font-semibold rounded-lg hover:bg-csl-dark-green transition-colors duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-transform text-center"
            >
              <span className="text-center">{t('builder.navCta')}</span>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="xl:hidden p-2 rounded-lg text-gray-600 hover:text-csl-green hover:bg-gray-100 active:scale-95 transition-transform"
            aria-label={isMenuOpen ? t('header.closeMenu', 'Close menu') : t('header.openMenu', 'Open menu')}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="xl:hidden border-t border-gray-100 bg-white animate-scale-in overflow-hidden max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item) =>
                item.isRoute ? (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block px-3 py-2 text-gray-700 hover:text-csl-green hover:bg-gray-50 rounded-md transition-colors duration-300"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ) : (
                  <a
                    key={item.name}
                    href={item.href}
                    className="block px-3 py-2 text-gray-700 hover:text-csl-green hover:bg-gray-50 rounded-md transition-colors duration-300"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </a>
                )
              )}

              {/* Mobile Language Switcher */}
              <div className="flex items-center px-3 py-2 space-x-4 text-gray-700">
                <GlobeAltIcon className="h-5 w-5" />
                <div className="flex space-x-4">
                  <button
                    onClick={() => changeLanguage('fr')}
                    className={`${i18n.language.startsWith('fr') ? 'text-csl-green font-bold' : ''}`}
                  >
                    Français
                  </button>
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`${i18n.language.startsWith('en') ? 'text-csl-green font-bold' : ''}`}
                  >
                    English
                  </button>
                </div>
              </div>

              <a
                href={BUILDER_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent('Lead', { customData: { content_name: 'builder-header-cta' } });
                  setIsMenuOpen(false);
                }}
                className="block w-full mt-4 px-4 py-2 bg-csl-green text-white font-semibold rounded-lg hover:bg-csl-dark-green transition-colors duration-300 text-center"
              >
                {t('builder.navCta')}
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
